import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Search } from "lucide-react";
import api from "@/lib/api";
import StateBadge from "@/components/StateBadge";
import EmptyState from "@/components/EmptyState";
import { PROPERTY_STATE_LABELS } from "@/lib/dictionaries";

export default function PortalProperties() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");

    useEffect(() => {
        api.get("/portal/properties")
            .then((r) => setItems(r.data || []))
            .finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        return items.filter((p) => {
            if (filter !== "all" && p.state !== filter) return false;
            if (search) {
                const s = search.toLowerCase();
                if (
                    !(p.name || "").toLowerCase().includes(s) &&
                    !(p.code || "").toLowerCase().includes(s) &&
                    !(p.address || "").toLowerCase().includes(s)
                )
                    return false;
            }
            return true;
        });
    }, [items, filter, search]);

    const statesInUse = useMemo(() => {
        const set = new Set(items.map((p) => p.state).filter(Boolean));
        return Array.from(set);
    }, [items]);

    return (
        <div className="space-y-6" data-testid="portal-properties-page">
            <div>
                <p className="text-[11px] font-semibold tracking-widest uppercase text-zentra-accent-strong">
                    Tus propiedades
                </p>
                <h2 className="font-display text-3xl font-bold text-zentra-ink tracking-tight mt-1">
                    Mis propiedades
                </h2>
                <p className="text-sm text-zentra-muted mt-1">
                    {items.length} en gestión · {filtered.length} mostradas
                </p>
            </div>

            <div className="zentra-card-static p-3 flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2 px-3 py-2 bg-zentra-bg border border-zentra-border rounded-md text-sm flex-1 min-w-[200px]">
                    <Search size={14} className="text-zentra-muted" />
                    <input
                        className="bg-transparent outline-none border-0 flex-1 text-sm"
                        placeholder="Buscar por nombre, código, dirección…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        data-testid="portal-property-search"
                    />
                </div>
                <div className="flex items-center gap-1.5 overflow-x-auto">
                    <FilterChip active={filter === "all"} onClick={() => setFilter("all")} label="Todas" />
                    {statesInUse.map((s) => (
                        <FilterChip
                            key={s}
                            active={filter === s}
                            onClick={() => setFilter(s)}
                            label={PROPERTY_STATE_LABELS[s]?.label || s}
                        />
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="zentra-skeleton h-44" />
                    ))}
                </div>
            ) : filtered.length === 0 ? (
                <EmptyState
                    icon={Building2}
                    title="Sin propiedades"
                    description="Tu gestor no ha asignado propiedades a tu portal todavía."
                />
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="portal-property-grid">
                    {filtered.map((p) => (
                        <Link
                            key={p.id}
                            to={`/portal/propiedades/${p.id}`}
                            className="zentra-card p-5 flex flex-col gap-3 group"
                            data-testid={`portal-prop-${p.code}`}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="text-[11px] font-mono-zentra tracking-wider text-zentra-muted uppercase">
                                        {p.code}
                                    </p>
                                    <h3 className="font-display font-semibold text-zentra-ink leading-tight mt-0.5 line-clamp-1">
                                        {p.name}
                                    </h3>
                                </div>
                                <StateBadge value={p.state} dict={PROPERTY_STATE_LABELS} />
                            </div>
                            <p className="text-sm text-zentra-muted line-clamp-1">{p.address || "Sin dirección"}</p>
                            <div className="flex items-center gap-3 mt-auto pt-3 border-t border-zentra-border text-xs text-zentra-muted">
                                <span>{p.bedrooms || 0} hab</span>
                                <span>•</span>
                                <span>{p.bathrooms || 0} baños</span>
                                <span>•</span>
                                <span>{p.capacity || 0} pax</span>
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}

function FilterChip({ active, onClick, label }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition border ${
                active
                    ? "bg-zentra-primary text-white border-zentra-primary"
                    : "bg-white text-zentra-ink border-zentra-border hover:border-zentra-olive"
            }`}
        >
            {label}
        </button>
    );
}
